import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Eye, EyeOff, Save, Loader2, KeyRound, Bot, Globe, Plus, X, Shield } from 'lucide-react';
import { getSettings, updateSettings } from '../../lib/api';

export function SecuritySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [newIp, setNewIp] = useState('');
  const [newOrigin, setNewOrigin] = useState('');
  const [config, setConfig] = useState({
    recaptcha_enabled: false,
    recaptcha_site_key: '',
    recaptcha_secret_key: '',
    magic_link_security_code: '',
    ip_allowlist_enabled: false,
    ip_allowlist: [],
    cors_allowed_origins: [],
    cloudflare_only_enabled: false,
    backup_encryption_enabled: false,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await getSettings();
      const s = res.data || {};
      setConfig({
        recaptcha_enabled: s.recaptcha_enabled || false,
        recaptcha_site_key: s.recaptcha_site_key || '',
        recaptcha_secret_key: s.recaptcha_secret_key || '',
        magic_link_security_code: s.magic_link_security_code || '',
        ip_allowlist_enabled: s.ip_allowlist_enabled || false,
        ip_allowlist: s.ip_allowlist || [],
        cors_allowed_origins: s.cors_allowed_origins || [],
        cloudflare_only_enabled: s.cloudflare_only_enabled || false,
        backup_encryption_enabled: s.backup_encryption_enabled || false,
      });
    } catch {
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(config);
      toast.success('Security settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleShow = (field) => setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));

  const addIp = () => {
    const ip = newIp.trim();
    if (!ip) return;
    // Basic validation for IP or CIDR
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    if (!ipRegex.test(ip)) {
      toast.error('Invalid IP address or CIDR format (e.g., 192.168.1.1 or 10.0.0.0/24)');
      return;
    }
    if (config.ip_allowlist.includes(ip)) {
      toast.error('IP already in list');
      return;
    }
    setConfig(prev => ({ ...prev, ip_allowlist: [...prev.ip_allowlist, ip] }));
    setNewIp('');
  };

  const removeIp = (ip) => {
    setConfig(prev => ({ ...prev, ip_allowlist: prev.ip_allowlist.filter(i => i !== ip) }));
  };

  const addOrigin = () => {
    const origin = newOrigin.trim();
    if (!origin) return;
    // Basic URL validation
    if (!origin.match(/^https?:\/\/.+/)) {
      toast.error('Origin must start with http:// or https:// (e.g., https://yourdomain.com)');
      return;
    }
    // Remove trailing slash
    const cleaned = origin.replace(/\/+$/, '');
    if (config.cors_allowed_origins.includes(cleaned)) {
      toast.error('Origin already in list');
      return;
    }
    setConfig(prev => ({ ...prev, cors_allowed_origins: [...prev.cors_allowed_origins, cleaned] }));
    setNewOrigin('');
  };

  const removeOrigin = (origin) => {
    setConfig(prev => ({ ...prev, cors_allowed_origins: prev.cors_allowed_origins.filter(o => o !== origin) }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-settings">
      {/* reCAPTCHA Settings */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-base">reCAPTCHA v3 Protection</CardTitle>
          </div>
          <CardDescription>Protect login and password reset forms from bots</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Enable reCAPTCHA</Label>
              <p className="text-xs text-muted-foreground">Requires valid Google reCAPTCHA v3 keys</p>
            </div>
            <Switch
              checked={config.recaptcha_enabled}
              onCheckedChange={(v) => setConfig(prev => ({ ...prev, recaptcha_enabled: v }))}
              data-testid="switch-recaptcha-enabled"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="recaptcha-site-key">Site Key</Label>
            <div className="relative">
              <Input
                id="recaptcha-site-key"
                value={config.recaptcha_site_key}
                onChange={(e) => setConfig(prev => ({ ...prev, recaptcha_site_key: e.target.value }))}
                placeholder="6Lxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                type={showSecrets.site_key ? 'text' : 'password'}
                data-testid="input-recaptcha-site-key"
              />
              <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3"
                onClick={() => toggleShow('site_key')}>
                {showSecrets.site_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="recaptcha-secret-key">Secret Key</Label>
            <div className="relative">
              <Input
                id="recaptcha-secret-key"
                value={config.recaptcha_secret_key}
                onChange={(e) => setConfig(prev => ({ ...prev, recaptcha_secret_key: e.target.value }))}
                placeholder="6Lxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                type={showSecrets.secret_key ? 'text' : 'password'}
                data-testid="input-recaptcha-secret-key"
              />
              <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3"
                onClick={() => toggleShow('secret_key')}>
                {showSecrets.secret_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Get your keys from <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google reCAPTCHA Admin Console</a>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Magic Link Security Code */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-violet-600" />
            <CardTitle className="text-base">Password Reset Security Code</CardTitle>
          </div>
          <CardDescription>A shared code that users must enter to request a password reset magic link. Share this code only with trusted employees.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="security-code">Security Code</Label>
            <div className="relative">
              <Input
                id="security-code"
                value={config.magic_link_security_code}
                onChange={(e) => setConfig(prev => ({ ...prev, magic_link_security_code: e.target.value }))}
                placeholder="Enter a memorable security code"
                type={showSecrets.security_code ? 'text' : 'password'}
                data-testid="input-magic-link-security-code"
              />
              <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3"
                onClick={() => toggleShow('security_code')}>
                {showSecrets.security_code ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Users will need this code + their email to request a password reset link from the login page
            </p>
          </div>
        </CardContent>
      </Card>

      {/* CORS Allowed Origins */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-600" />
            <CardTitle className="text-base">CORS Allowed Origins</CardTitle>
          </div>
          <CardDescription>
            Control which domains can make API requests to this application. When empty, all origins are allowed (wildcard). Add your production domain(s) to restrict cross-origin access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Allowed Origins</Label>
            <div className="flex gap-2">
              <Input
                value={newOrigin}
                onChange={(e) => setNewOrigin(e.target.value)}
                placeholder="e.g., https://yourdomain.com"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOrigin())}
                data-testid="input-cors-origin"
              />
              <Button type="button" variant="outline" size="icon" onClick={addOrigin} data-testid="btn-add-cors-origin">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {config.cors_allowed_origins.length > 0 ? (
              <div className="space-y-1 mt-2">
                {config.cors_allowed_origins.map((origin, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-muted/50 rounded text-sm font-mono">
                    <span>{origin}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeOrigin(origin)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No origins configured — all origins are currently allowed (wildcard *).</p>
            )}
            <p className="text-xs text-muted-foreground">
              Tip: Include your full domain with protocol (e.g., <code className="bg-muted px-1 rounded">https://app.example.com</code>). Changes take effect within 60 seconds.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* IP Allowlist */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-600" />
            <CardTitle className="text-base">IP Allowlist</CardTitle>
          </div>
          <CardDescription>Restrict API access to specific IP addresses or CIDR ranges. When enabled, only listed IPs can access the system.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Enable IP Allowlist</Label>
              <p className="text-xs text-muted-foreground">Warning: Make sure your IP is in the list before enabling</p>
            </div>
            <Switch
              checked={config.ip_allowlist_enabled}
              onCheckedChange={(v) => setConfig(prev => ({ ...prev, ip_allowlist_enabled: v }))}
              data-testid="switch-ip-allowlist-enabled"
            />
          </div>

          <div className="space-y-2">
            <Label>Allowed IPs / CIDR Ranges</Label>
            <div className="flex gap-2">
              <Input
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                placeholder="e.g., 203.0.113.50 or 10.0.0.0/24"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addIp())}
                data-testid="input-ip-allowlist"
              />
              <Button type="button" variant="outline" size="icon" onClick={addIp} data-testid="btn-add-ip">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {config.ip_allowlist.length > 0 ? (
              <div className="space-y-1 mt-2">
                {config.ip_allowlist.map((ip, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-muted/50 rounded text-sm font-mono">
                    <span>{ip}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeIp(ip)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No IPs configured. When enabled with an empty list, all traffic will be blocked.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cloudflare-Only Access */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-600" />
            <CardTitle className="text-base">Cloudflare-Only Access</CardTitle>
          </div>
          <CardDescription>Block direct server access. Only requests routed through Cloudflare will be accepted. Enable this after confirming your domain is proxied through Cloudflare.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Require Cloudflare Headers</Label>
              <p className="text-xs text-muted-foreground">Blocks requests missing CF-Connecting-IP and CF-Ray headers</p>
            </div>
            <Switch
              checked={config.cloudflare_only_enabled}
              onCheckedChange={(v) => setConfig(prev => ({ ...prev, cloudflare_only_enabled: v }))}
              data-testid="switch-cloudflare-only"
            />
          </div>
        </CardContent>
      </Card>

      {/* Backup Encryption */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" />
            <CardTitle className="text-base">Backup Encryption</CardTitle>
          </div>
          <CardDescription>Encrypt backup files at rest using AES-256-CBC. Encrypted backups can only be restored by this application instance.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Encrypt backups at rest</Label>
              <p className="text-xs text-muted-foreground">New backups will be AES-256 encrypted. Existing backups are not affected.</p>
            </div>
            <Switch
              checked={config.backup_encryption_enabled}
              onCheckedChange={(v) => setConfig(prev => ({ ...prev, backup_encryption_enabled: v }))}
              data-testid="switch-backup-encryption"
            />
          </div>
        </CardContent>
      </Card>

      {/* Credential Encryption Info */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-teal-600" />
            <CardTitle className="text-base">Credential Encryption</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>The following credentials are automatically encrypted in the database (AES-256-GCM):</p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li><span className="font-mono text-xs">SMTP Password</span> — Encrypted when saved via Settings</li>
              <li><span className="font-mono text-xs">FTP/SFTP Password</span> — Encrypted when saved via Backup Settings</li>
            </ul>
            <p className="pt-2 text-xs">Credentials are decrypted in-memory only when needed (sending email, connecting to FTP). They are never returned to the browser in plaintext.</p>
          </div>
        </CardContent>
      </Card>

      {/* Rate Limiting Info */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-base">Rate Limiting</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Active rate limits (enforced automatically):</p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li><span className="font-mono text-xs">Login</span> — 10 requests/minute per IP</li>
              <li><span className="font-mono text-xs">Password Reset</span> — 5 requests/minute per IP</li>
              <li><span className="font-mono text-xs">Tenant Setup</span> — 3 requests/minute per IP</li>
            </ul>
            <p className="pt-2 text-xs">Exceeding limits returns HTTP 429. Limits are per real client IP (Cloudflare-aware).</p>
          </div>
        </CardContent>
      </Card>

      {/* Security Headers Info */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-600" />
            <CardTitle className="text-base">Security Headers</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>The following headers are automatically applied to all API responses:</p>
            <ul className="list-disc pl-5 space-y-0.5 text-xs font-mono">
              <li>Strict-Transport-Security (HSTS, 1 year)</li>
              <li>X-Content-Type-Options: nosniff</li>
              <li>X-Frame-Options: DENY</li>
              <li>X-XSS-Protection: 1; mode=block</li>
              <li>Referrer-Policy: strict-origin-when-cross-origin</li>
              <li>Permissions-Policy: camera=(), microphone=(), geolocation=()</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="btn-save-security-settings">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Security Settings
        </Button>
      </div>
    </div>
  );
}
